'use client';
import React, { useEffect, useState } from 'react';
import AppLayout from '@/components/AppLayout';
import SubmissionForm from './components/SubmissionForm';
import Breadcrumb from './components/Breadcrumb';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';
import { LogIn, ScrollText } from 'lucide-react';
import ContributorWalkthrough, { WALKTHROUGH_KEY } from '@/components/ContributorWalkthrough';

export default function ContributionSubmissionPage() {
  const { user, loading } = useAuth();
  const [showWalkthrough, setShowWalkthrough] = useState(false);

  useEffect(() => {
    if (user) {
      const seen = localStorage.getItem(WALKTHROUGH_KEY);
      if (!seen) {
        setShowWalkthrough(true);
      }
    }
  }, [user]);

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background)' }}>
          <div className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
        </div>
      </AppLayout>
    );
  }

  if (!user) {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center" style={{ backgroundColor: 'var(--background)' }}>
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
            style={{ backgroundColor: 'rgba(193,122,62,0.12)', border: '1px solid rgba(193,122,62,0.25)' }}
          >
            <ScrollText size={28} style={{ color: 'var(--primary)' }} />
          </div>
          <h1 className="text-2xl font-bold mb-3" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}>
            Sign in to contribute
          </h1>
          <p className="text-sm max-w-sm mb-8" style={{ color: 'var(--muted-foreground)' }}>
            You need an account to submit a contribution to the Griott archive. Browsing and reading published contributions is always free — no sign-up required.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Link
              href="/sign-up-login"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all active:scale-95"
              style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
            >
              <LogIn size={15} />
              Sign in or create account
            </Link>
            <Link
              href="/contributions"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium border transition-all"
              style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
            >
              Browse contributions
            </Link>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {showWalkthrough && (
        <ContributorWalkthrough onClose={() => setShowWalkthrough(false)} />
      )}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 py-8">
        <Breadcrumb />
        <SubmissionForm />
      </div>
    </AppLayout>
  );
}